import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getRouteImageAlt } from '../../utils/routeImageAlt';
import { getRouteLocationDisplay } from 'shared/utils/routeLocationDisplay';

/** Длительность в секундах → ISO 8601 (PT1H2M30S) для VideoObject */
const secondsToISO8601Duration = (seconds) => {
  if (seconds == null || isNaN(seconds) || seconds < 0) return undefined;
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  let str = 'PT';
  if (h) str += `${h}H`;
  if (m) str += `${m}M`;
  if (sec || str === 'PT') str += `${sec}S`;
  return str;
};

/** Из watch-URL YouTube (или youtu.be) получаем embed-URL для VideoObject.embedUrl */
const getVideoEmbedUrl = (watchUrl) => {
  if (!watchUrl || typeof watchUrl !== 'string') return watchUrl;
  const u = watchUrl.trim();
  const ytMatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  return u;
};

/** Локализованные название и описание маршрута по текущему языку (для JSON-LD) */
const getLocalizedRouteTitle = (route, lang) => {
  if (!route) return '';
  const tr = route.title_translations && typeof route.title_translations === 'object'
    ? (route.title_translations[lang] || route.title_translations.en || route.title_translations.ru)
    : null;
  return (tr && String(tr).trim()) || (route.title && String(route.title).trim()) || '';
};

const getLocalizedRouteDescription = (route, lang) => {
  if (!route) return '';
  const tr = route.description_translations && typeof route.description_translations === 'object'
    ? (route.description_translations[lang] || route.description_translations.en || route.description_translations.ru)
    : null;
  return (tr && String(tr).trim()) || (route.description && String(route.description).trim()) || '';
};

/**
 * SEO компонент для управления meta tags, canonical URL, структурированными данными и hreflang
 * Автоматически генерирует canonical URL на основе текущего pathname
 * 
 * @param {string} title - Заголовок страницы
 * @param {string} description - Описание страницы
 * @param {string} image - URL изображения для превью
 * @param {number} imageWidth - Ширина превью для og:image (влияет на размер в мессенджерах)
 * @param {number} imageHeight - Высота превью для og:image
 * @param {string} twitterCard - 'summary' (компактная карточка, маленькое превью) или 'summary_large_image'
 * @param {string} type - Тип контента (website, article, video, profile)
 * @param {object} route - Данные маршрута для TouristTrip schema (в т.ч. duration, created_at, updated_at)
 * @param {string} routeStartPlaceName - Название населённого пункта начала маршрута (для tripOrigin)
 * @param {string} routeEndPlaceName - Название населённого пункта конца маршрута (для tripDestination)
 * @param {string} routeVideoAuthor - Автор видео/канал (YouTube и др.), подтянутый из метаданных (для FAQ)
 * @param {object} video - Данные видео для VideoObject schema
 * @param {object} profile - Данные профиля для ProfilePage schema
 * @param {array} videos - Массив видео для автоматического выбора thumbnail
 * @param {array} breadcrumbs - Массив хлебных крошек для BreadcrumbList schema
 * @param {array} faqItems - Массив { name: string, text: string } для FAQPage (например, главная страница)
 * @param {boolean} includeWebSite - Добавить схему WebSite с SearchAction (только для главной)
 * @param {boolean} includeOrganization - Добавить схему Organization с logo (только для главной)
 * @param {ReactNode} children - Дополнительные meta tags
 */
const SEO = ({ 
  title, 
  description, 
  image, 
  imageWidth = 1200, 
  imageHeight = 630, 
  twitterCard = 'summary_large_image', 
  type = 'website', 
  route,
  routeStartPlaceName,
  routeEndPlaceName,
  routeVideoAuthor,
  video,
  profile,
  videos,
  breadcrumbs,
  faqItems,
  includeWebSite = false,
  includeOrganization = false,
  children
}) => {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const localeForDate = lang === 'ru' ? 'ru-RU' : 'en-US';
  // Схема (JSON-LD) всегда на английском — не зависит от региона/языка пользователя
  const schemaLang = 'en';
  const schemaLocaleForDate = 'en-US';
  const tEn = (key, opts) => t(key, { ...opts, lng: schemaLang });
  
  // Нормализация пути: без завершающего слэша; /index.html считаем главной (canonical = /)
  let pathNormalized = location.pathname.replace(/\/+$/, '') || '/';
  if (pathNormalized === '/index.html') pathNormalized = '/';
  // Защита от битого пути (например /http://travmap.org/): путь не должен содержать протокол
  if (pathNormalized.startsWith('/http') || pathNormalized.includes('://')) {
    pathNormalized = '/';
  }
  // Канонический URL для /route/:id и /video/:id — всегда без сегмента языка (/ru, /en),
  const routeOrVideoWithLang = pathNormalized.match(/^\/(route|video)\/(\d+)\/(ru|en)$/);
  if (routeOrVideoWithLang) {
    pathNormalized = `/${routeOrVideoWithLang[1]}/${routeOrVideoWithLang[2]}`;
  }
  // Используем текущий origin для canonical, чтобы не было «неканоническая» при открытии через www
  const siteOrigin = typeof window !== 'undefined' && window.location.origin &&
    (window.location.origin === 'https://travmap.org' || window.location.origin === 'https://www.travmap.org')
    ? window.location.origin
    : 'https://travmap.org';
  const canonicalUrl = `${siteOrigin}${pathNormalized}`;
  
  // Используем title из props или дефолтный
  const pageTitle = title || 'Travel Map Platform';
  const fullTitle = title ? `${title} | Travel Map Platform` : 'Travel Map Platform';
  
  // Дефолтные значения — английский для индексации и превью (EU/поисковики)
  const defaultDescription = 'Travel videos on the world map. Real routes and travelers\' stories linked to specific places.';
  const defaultImage = 'https://travmap.org/icons/icon-192x192.png';
  
  const pageDescription = description || defaultDescription;
  
  // Автоматический выбор изображения по приоритету:
  // 1. Явно указанное изображение (prop image)
  // 2. Обложка маршрута (route.cover_image)
  // 3. Thumbnail первого видео (videos[0].thumbnail)
  // 4. Thumbnail видео (video.thumbnail)
  // 5. Аватар профиля (profile.avatar_url)
  // 6. Дефолтное изображение
  const pageImage = useMemo(() => {
    if (image) return image;
    if (route?.cover_image) return route.cover_image;
    if (videos && videos.length > 0 && videos[0]?.thumbnail) return videos[0].thumbnail;
    if (video?.thumbnail) return video.thumbnail;
    if (profile?.avatar_url) return profile.avatar_url;
    return defaultImage;
  }, [image, route?.cover_image, videos, video?.thumbnail, profile?.avatar_url, defaultImage]);

  // Абсолютный URL изображения для og:image / twitter:image (мессенджеры требуют абсолютный URL)
  const pageImageAbsolute = useMemo(() => {
    const url = typeof pageImage === 'string' ? pageImage : '';
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://travmap.org${url.startsWith('/') ? '' : '/'}${url}`;
  }, [pageImage]);

  // Генерация hreflang URL для всех доступных языков (по умолчанию en для индексации)
  const currentLang = i18n.language || 'en';
  
  // Получаем список всех доступных языков из i18next
  // Используем i18n.languages если доступно, иначе берем из resources
  const availableLanguages = useMemo(() => {
    // Пробуем получить из i18n.languages (список загруженных языков)
    if (i18n.languages && i18n.languages.length > 0) {
      return i18n.languages;
    }
    // Иначе берем из resources (все языки, для которых есть переводы)
    if (i18n.options && i18n.options.resources) {
      return Object.keys(i18n.options.resources);
    }
    // Fallback на дефолтные языки
    return ['ru', 'en'];
  }, [i18n.languages, i18n.options?.resources]);
  
  // Путь для hreflang (тот же нормализованный путь, что и для canonical)
  const basePath = pathNormalized;
  
  // Функция для генерации URL для конкретного языка
  // Для /route/:id, /video/:id, /profile/:id — alternates: /route/127 (en), /route/127/ru (совпадает с реальными маршрутами приложения)
  // Иначе Google индексирует несуществующие /ru/route/127 и канонические версии расходятся в GSC
  const generateLangUrl = (lang) => {
    let cleanPath = basePath;
    availableLanguages.forEach(l => {
      const langPrefix = `/${l}`;
      if (cleanPath.startsWith(langPrefix + '/') || cleanPath === langPrefix) {
        cleanPath = cleanPath.replace(langPrefix, '') || '/';
      }
    });
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    const entityWithId = cleanPath.match(/^\/(route|video|profile)\/(\d+)$/);
    if (entityWithId) {
      if (lang === 'en') return `https://travmap.org${cleanPath}`;
      return `https://travmap.org${cleanPath}/${lang}`;
    }
    return `https://travmap.org/${lang}${cleanPath === '/' ? '' : cleanPath}`;
  };
  
  // Генерируем hreflang теги для всех языков
  const hreflangUrls = useMemo(() => {
    return availableLanguages.map(lang => ({
      lang,
      url: generateLangUrl(lang)
    }));
  }, [availableLanguages, basePath]);
  
  // Генерация JSON-LD структурированных данных
  const structuredData = useMemo(() => {
    const data = [];
    const siteBaseUrl = canonicalUrl.replace(/\/$/, '') || 'https://travmap.org';
    const isHomePage = pathNormalized === '/';

    // WebSite + SearchAction и Organization — только на главной (фаза 1.1, 1.2)
    // Один блок WebSite (с description и publisher), чтобы не дублировать с другими источниками (index.html и т.д.)
    if (isHomePage) {
      if (includeWebSite) {
        data.push({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": siteBaseUrl,
          "name": "Travel Map Platform",
          "description": "Explore the world through real video routes or pin your own travels to the map! Discover hidden gems, plan custom trips, and join a global community of creators.",
          "about": [
            {
              "@type": "AboutPage",
              "name": "About Travel Map Platform",
              "url": `${siteBaseUrl}/about`
            },
            {
              "@type": "WebPage",
              "name": "For Creators",
              "description": "For content creators: add videos and routes to the map",
              "url": `${siteBaseUrl}/for-creators`
            },
            {
              "@type": "ContactPage",
              "name": "Contact / Feedback",
              "url": `${siteBaseUrl}/contact-us`
            },
            {
              "@type": "WebPage",
              "name": "FAQ",
              "url": `${siteBaseUrl}/faq`
            }
          ],
          "publisher": {
            "@type": "Organization",
            "name": "Travel Map Platform",
            "url": siteBaseUrl,
            "logo": `${siteBaseUrl}/icons/icon-192x192.png`,
            "sameAs": [
              "https://www.facebook.com/travelmapplatform",
              "https://t.me/TravelMapPlatform"
            ]
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteBaseUrl}/feed?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        });
      }
      if (includeOrganization) {
        data.push({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Travel Map Platform",
          "url": siteBaseUrl,
          "logo": `${siteBaseUrl}/icons/icon-192x192.png`,
          "email": "travelmapplatform@gmail.com",
          "sameAs": [
            "https://www.facebook.com/travelmapplatform",
            "https://t.me/TravelMapPlatform"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "travelmapplatform@gmail.com",
            "contactType": "customer support"
          }
        });
      }
    }

    // TouristTrip только на странице маршрута (/route/:id), не на странице видео
    // На странице видео выводим только VideoObject; данные маршрута используются в contentLocation и ImageObject
    if (route && !video) {
      const routeType = (route.route_type || route.routeType || 'route').toLowerCase();
      const localizedTitle = getLocalizedRouteTitle(route, schemaLang) || route.title || '';
      const localizedDesc = getLocalizedRouteDescription(route, schemaLang) || route.description || '';
      const startPlace = routeStartPlaceName && typeof routeStartPlaceName === 'string' ? routeStartPlaceName.trim() : '';
      const endPlace = routeEndPlaceName && typeof routeEndPlaceName === 'string' ? routeEndPlaceName.trim() : '';
      const markerLat = route.marker_lat ?? route.markerLat;
      const markerLng = route.marker_lng ?? route.markerLng;
      const endLat = route.end_lat ?? route.endLat;
      const endLng = route.end_lng ?? route.endLng;
      const lat = (markerLat != null && markerLng != null) ? Number(markerLat) : (endLat != null ? Number(endLat) : NaN);
      const lng = (markerLat != null && markerLng != null) ? Number(markerLng) : (endLng != null ? Number(endLng) : NaN);
      const routeId = route.id ?? route.ID;
      const routeMapUrl = routeId != null ? `https://travmap.org/route/${routeId}` : null;

      // type === "place" → только Place (одна точка по schema.org)
      if (routeType === 'place') {
        const placeData = {
          "@context": "https://schema.org",
          "@type": "Place",
          "name": localizedTitle || tEn('seo.routeMap'),
          "url": canonicalUrl
        };
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          placeData.geo = { "@type": "GeoCoordinates", "latitude": Math.round(lat * 10000) / 10000, "longitude": Math.round(lng * 10000) / 10000 };
        }
        if (routeMapUrl) placeData.hasMap = routeMapUrl;
        if (route.cover_image) {
          const img = route.cover_image;
          placeData.image = img.startsWith('http') ? img : `https://travmap.org${img.startsWith('/') ? '' : '/'}${img}`;
        }
        data.push(placeData);
      } else {
      // type === "places" (несколько точек без линии) или "route" (road_route, river_route, direct_route) → TouristTrip + itinerary; линия GeoShape только для route
      let description = localizedDesc;
      if (route.distance != null && route.distance !== '' && Number(route.distance) > 0) {
        const distKm = typeof route.distance === 'number' ? (route.distance / 1000).toFixed(1) : route.distance;
        description = description ? `${description} ${tEn('seo.descriptionDistance', { value: distKm })}` : tEn('seo.descriptionDistance', { value: distKm });
      }
      const createdAt = route.created_at ?? route.createdAt;
      if (createdAt) {
        const dateStrLong = new Date(createdAt).toLocaleDateString(schemaLocaleForDate, { year: 'numeric', month: 'long', day: 'numeric' });
        description = description ? `${description} ${tEn('seo.descriptionPublished', { date: dateStrLong })}` : tEn('seo.descriptionPublished', { date: dateStrLong });
      }
      const durationMinutes = route.duration != null ? Number(route.duration) : NaN;
      if (!isNaN(durationMinutes) && durationMinutes > 0) {
        const hours = Math.floor(durationMinutes / 60);
        const minutes = Math.round(durationMinutes % 60);
        const durText = hours >= 24
          ? tEn('seo.faq.durationDays', { d: Math.floor(hours / 24) })
          : (hours > 0 ? tEn('seo.faq.durationHoursMin', { h: hours, m: minutes }) : tEn('seo.faq.durationMin', { m: minutes }));
        description = description ? `${description} ${tEn('seo.descriptionDuration', { value: durText })}` : tEn('seo.descriptionDuration', { value: durText });
      }
      if (routeStartPlaceName && typeof routeStartPlaceName === 'string' && routeStartPlaceName.trim() && routeEndPlaceName && typeof routeEndPlaceName === 'string' && routeEndPlaceName.trim()) {
        const fromTo = tEn('seo.descriptionFromTo', { start: routeStartPlaceName.trim(), end: routeEndPlaceName.trim() });
        description = description ? `${description} ${fromTo}` : fromTo;
      }

      const startPlace = routeStartPlaceName && typeof routeStartPlaceName === 'string' ? routeStartPlaceName.trim() : '';
      const endPlace = routeEndPlaceName && typeof routeEndPlaceName === 'string' ? routeEndPlaceName.trim() : '';
      const startEndDiffer = startPlace && endPlace && startPlace.toLowerCase() !== endPlace.toLowerCase();
      const routeLoc = getRouteLocationDisplay(route, schemaLang || 'en');
      const country = routeLoc.country;
      const cityOrRegion = routeLoc.cityOrRegion;
      // Имя Place для блока «карта маршрута» — географическое (from–to, country), не название маршрута
      let routeMapPlaceName = '';
      if (startEndDiffer && country) {
        routeMapPlaceName = tEn('seo.routeMapPlaceName', { start: startPlace, end: endPlace, country });
      } else if (startPlace && country) {
        routeMapPlaceName = tEn('seo.routeMapPlaceNameSingle', { place: startPlace, country });
      } else if (country) {
        routeMapPlaceName = cityOrRegion ? `${cityOrRegion}, ${country}` : tEn('seo.routeMapPlaceNameCountryOnly', { country });
      }
      if (!routeMapPlaceName) routeMapPlaceName = tEn('seo.routeMap');

      const routeData = {
        "@context": "https://schema.org",
        "@type": "TouristTrip",
        "name": startEndDiffer ? tEn('seo.faq.routeNameFromTo', { start: startPlace, end: endPlace }) : (localizedTitle || ''),
        "description": description || undefined,
        "url": canonicalUrl,
      };
      if (startPlace) {
        routeData.tripOrigin = { "@type": "Place", "name": startPlace };
      }
      // Конечная точка: в schema.org у Trip нет arrivalLocation — оставляем только в description и FAQ

      // Itinerary — массив Place из path (названия точек) или один элемент регион/страна
      let itineraryPlaces = [];
      try {
        const pathRaw = route.path;
        if (pathRaw) {
          const path = typeof pathRaw === 'string' ? JSON.parse(pathRaw || '[]') : pathRaw;
          if (Array.isArray(path) && path.length > 0) {
            let prevName = '';
            path.forEach((p) => {
              // Схема только на английском: не подставляем .ru, только .en
              const tr = p.point_title_translations && typeof p.point_title_translations === 'object'
                ? (p.point_title_translations[schemaLang] || p.point_title_translations.en)
                : null;
              const name = (tr && String(tr).trim()) || (p.pointTitle && String(p.pointTitle).trim()) || (p.point_title && String(p.point_title).trim())
                || [p.cityOrRegion || p.city_or_region, p.country].filter(Boolean).map(String).join(', ').trim();
              const placeName = name || [cityOrRegion, country].filter(Boolean).join(', ');
              if (placeName && placeName !== prevName) {
                itineraryPlaces.push({ "@type": "Place", "name": placeName });
                prevName = placeName;
              }
            });
          }
        }
      } catch (_) { /* ignore */ }
      if (itineraryPlaces.length === 0 && (country || cityOrRegion)) {
        itineraryPlaces = [{ "@type": "Place", "name": [cityOrRegion, country].filter(Boolean).join(', ') }];
      }
      if (itineraryPlaces.length > 0) {
        routeData.itinerary = itineraryPlaces;
      }

      // Координаты метки/конца — в первый Place в itinerary
      if (Number.isFinite(lat) && Number.isFinite(lng) && itineraryPlaces.length > 0) {
        itineraryPlaces[0].geo = { "@type": "GeoCoordinates", "latitude": Math.round(lat * 10000) / 10000, "longitude": Math.round(lng * 10000) / 10000 };
      }

      // Place с hasMap; GeoShape line только для типа "route" (road_route, river_route, direct_route), не для "places"
      let routeMapPlace = null;
      try {
        const pathRaw = route.path;
        if (pathRaw) {
          const path = typeof pathRaw === 'string' ? JSON.parse(pathRaw || '[]') : pathRaw;
          const isLineRoute = routeType === 'route' && Array.isArray(path) && path.length >= 2;
          if (isLineRoute) {
            const coords = [];
            path.forEach((p) => {
              const la = Number(p.lat ?? p.latitude);
              const ln = Number(p.lng ?? p.longitude ?? p.lon);
              if (Number.isFinite(la) && Number.isFinite(ln)) {
                coords.push(Math.round(la * 10000) / 10000, Math.round(ln * 10000) / 10000);
              }
            });
            if (coords.length >= 4) {
              routeMapPlace = {
                "@context": "https://schema.org",
                "@type": "Place",
                "name": routeMapPlaceName,
                "geo": { "@type": "GeoShape", "line": coords.join(' ') }
              };
              if (routeMapUrl) routeMapPlace.hasMap = routeMapUrl;
            }
          }
        }
      } catch (_) { /* ignore */ }
      if (!routeMapPlace && routeMapUrl) {
        routeMapPlace = {
          "@context": "https://schema.org",
          "@type": "Place",
          "name": routeMapPlaceName,
          "hasMap": routeMapUrl
        };
      }
      // Place с картой пушим после TouristTrip (отдельный блок JSON-LD)

      if (route.cover_image) {
        const img = route.cover_image;
        routeData.image = img.startsWith('http') ? img : `https://travmap.org${img.startsWith('/') ? '' : '/'}${img}`;
      }
      const routeUser = route.user || route.User;
      if (routeUser && (routeUser.id || routeUser.username)) {
        const name = [routeUser.firstName || routeUser.first_name, routeUser.lastName || routeUser.last_name].filter(Boolean).join(' ').trim() || routeUser.username || '';
        if (name) {
          routeData.provider = {
            "@type": "Person",
            "name": name,
            "url": `https://travmap.org/profile/${routeUser.id || routeUser.username}`
          };
        }
      }
      const tags = Array.isArray(route.tags) ? route.tags : (typeof route.tags === 'string' ? (() => { try { return JSON.parse(route.tags || '[]'); } catch { return []; } })() : []);
      if (tags.length > 0) {
        routeData.touristType = tags.filter(t => typeof t === 'string').slice(0, 10);
      }
      data.push(routeData);
      if (routeMapPlace) data.push(routeMapPlace);

      // ImageObject для обложки маршрута (description = alt по правилам SEO_alt.md)
      if (pageImageAbsolute) {
        const imageDescription = getRouteImageAlt(route, {
          routeStartPlaceName: startPlace || undefined,
          routeEndPlaceName: endPlace || undefined,
          locale: schemaLang
        });
        data.push({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          "contentUrl": pageImageAbsolute,
          "description": imageDescription || undefined
        });
      }

      // FAQPage: данные, которые нельзя добавить в Trip (datePublished, duration, начало/конец, расстояние) — в виде вопрос-ответ для расширенных сниппетов
      const faqItems = [];
      if (createdAt) {
        const dateStrLong = new Date(createdAt).toLocaleDateString(schemaLocaleForDate, { year: 'numeric', month: 'long', day: 'numeric' });
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.whenPublished'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": dateStrLong
          }
        });
      }
      if (!isNaN(durationMinutes) && durationMinutes > 0) {
        const hours = Math.floor(durationMinutes / 60);
        const minutes = Math.round(durationMinutes % 60);
        const durTextFaq = hours >= 24
          ? tEn('seo.faq.durationDays', { d: Math.floor(hours / 24) })
          : (hours > 0 ? tEn('seo.faq.durationHoursMin', { h: hours, m: minutes }) : tEn('seo.faq.durationMin', { m: minutes }));
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.duration'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": durTextFaq
          }
        });
      }
      const startEndSame = startPlace && endPlace && startPlace.toLowerCase() === endPlace.toLowerCase();
      if (startPlace) {
        if (startEndSame) {
          faqItems.push({
            "@type": "Question",
            "name": tEn('seo.faq.whereLocated'),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": startPlace
            }
          });
        } else {
          faqItems.push({
            "@type": "Question",
            "name": tEn('seo.faq.whereStart'),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": startPlace
            }
          });
        }
      }
      if (endPlace && !startEndSame) {
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.whereEnd'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": endPlace
          }
        });
      }
      if (startPlace && endPlace && !startEndSame) {
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.fromWhereToWhere'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": tEn('seo.faq.routeFromTo', { start: startPlace, end: endPlace })
          }
        });
      }
      if (route.distance != null && route.distance !== '' && Number(route.distance) > 0) {
        const distKm = typeof route.distance === 'number' ? (route.distance / 1000).toFixed(1) : route.distance;
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.distance'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": tEn('seo.faq.distanceValue', { value: distKm })
          }
        });
      }
      if (routeVideoAuthor && typeof routeVideoAuthor === 'string' && routeVideoAuthor.trim()) {
        faqItems.push({
          "@type": "Question",
          "name": tEn('seo.faq.videoAuthor'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": routeVideoAuthor.trim()
          }
        });
      }
      if (faqItems.length > 0) {
        data.push({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems
        });
      }
      if (videos && Array.isArray(videos) && videos.length > 0) {
        const videoListItems = videos.slice(0, 20).map((v, i) => {
          const vid = v.id ?? v.ID;
          const url = vid != null ? `https://travmap.org/video/${vid}` : undefined;
          const name = v.title ?? v.name ?? '';
          return url ? { "@type": "ListItem", "position": i + 1, "name": name, "item": url } : null;
        }).filter(Boolean);
        if (videoListItems.length > 0) {
          data.push({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": tEn('seo.videosOfRoute'),
            "itemListElement": videoListItems
          });
        }
      }
      } // конец else (type !== 'place')
    }

    // VideoObject для видео (GEO: author, spatialCoverage по маршруту)
    // Требования Google «страница просмотра»: embedUrl, contentUrl, duration в ISO 8601, thumbnailUrl абсолютный, uploadDate
    if (video) {
      const videoData = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": video.title || '',
        "description": video.description || '',
        "thumbnailUrl": pageImageAbsolute || pageImage,
        "url": canonicalUrl,
      };
      const uploadDateRaw = video.created_at ?? video.createdAt;
      if (uploadDateRaw) {
        videoData.uploadDate = new Date(uploadDateRaw).toISOString();
      }
      const durationISO = video.duration != null ? secondsToISO8601Duration(video.duration) : undefined;
      if (durationISO) {
        videoData.duration = durationISO;
      }
      if (video.url) {
        videoData.contentUrl = video.url;
        videoData.embedUrl = getVideoEmbedUrl(video.url);
      }
      const author = video.author || (() => {
        const u = video.user || video.User;
        if (!u || !(u.id || u.username)) return null;
        const name = [u.firstName || u.first_name, u.lastName || u.last_name].filter(Boolean).join(' ').trim() || u.username || '';
        return name ? { name, url: `https://travmap.org/profile/${u.id || u.username}` } : null;
      })();
      if (author && author.name) {
        videoData.author = { "@type": "Person", "name": author.name, "url": author.url || canonicalUrl };
      }
      if (route && (route.country || route.city_or_region || route.country_en || route.city_or_region_en)) {
        const videoRouteLoc = getRouteLocationDisplay(route, schemaLang || 'en');
        const placeName = [videoRouteLoc.cityOrRegion, videoRouteLoc.country].filter(Boolean).join(', ');
        if (placeName) {
          const markerLat = route.marker_lat ?? route.markerLat;
          const markerLng = route.marker_lng ?? route.markerLng;
          const endLat = route.end_lat ?? route.endLat;
          const endLng = route.end_lng ?? route.endLng;
          const lat = (markerLat != null && markerLng != null) ? Number(markerLat) : (endLat != null ? Number(endLat) : NaN);
          const lng = (markerLat != null && markerLng != null) ? Number(markerLng) : (endLng != null ? Number(endLng) : NaN);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            videoData.contentLocation = {
              "@type": "Place",
              "name": placeName,
              "geo": { "@type": "GeoCoordinates", "latitude": Math.round(lat * 1000) / 1000, "longitude": Math.round(lng * 1000) / 1000 }
            };
          } else {
            videoData.spatialCoverage = { "@type": "Place", "name": placeName };
          }
        }
      }
      const routeId = route && (route.id ?? route.ID);
      if (routeId != null) {
        videoData.isPartOf = { "@type": "WebPage", "url": `https://travmap.org/route/${routeId}` };
      }
      data.push(videoData);

      // ImageObject для превью видео (description = alt по правилам SEO_alt.md)
      if (video && pageImageAbsolute) {
        const imageDescription = route
          ? getRouteImageAlt(route, {
              routeStartPlaceName: route.routeStartPlaceName,
              routeEndPlaceName: route.routeEndPlaceName,
              locale: schemaLang
            })
          : (video.title && String(video.title).trim()) ? `${video.title.trim()} – travel video` : 'Travel video';
        data.push({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          "contentUrl": pageImageAbsolute,
          "description": imageDescription || undefined
        });
      }
    }
    
    // ProfilePage для профилей
    if (profile) {
      const profileData = {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "name": [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username || '',
        "url": canonicalUrl,
      };
      
      // Добавляем description если есть bio
      if (profile.bio) {
        profileData.description = profile.bio;
      }
      
      data.push(profileData);
    }
    
    // BreadcrumbList для навигации
    if (breadcrumbs && breadcrumbs.length > 0) {
      // Всегда начинаем с главной страницы (English для JSON-LD по умолчанию)
      const breadcrumbItems = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": tEn('navigation.home'),
          "item": "https://travmap.org/"
        }
      ];
      
      // Добавляем переданные breadcrumbs
      breadcrumbs.forEach((item, index) => {
        // Защита от битого item.path: не склеивать base URL с полным URL (http:// или https://)
        let itemUrl = canonicalUrl;
        if (item.path) {
          const p = String(item.path).trim();
          if (p.startsWith('http://') || p.startsWith('https://')) {
            itemUrl = p;
          } else if (!p.includes('://')) {
            itemUrl = `https://travmap.org${p.startsWith('/') ? p : '/' + p}`;
          }
        }
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": index + 2, // +2 потому что позиция 1 уже занята главной
          "name": item.label || item.name || '',
          "item": itemUrl
        });
      });
      
      const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      };
      
      data.push(breadcrumbData);
    }

    // FAQPage из переданного массива (например, главная страница — статистика платформы)
    if (faqItems && Array.isArray(faqItems) && faqItems.length > 0) {
      const mainEntity = faqItems
        .filter((item) => item && typeof item.name === 'string' && typeof item.text === 'string')
        .map((item) => ({
          "@type": "Question",
          "name": item.name.trim(),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.text.trim()
          }
        }));
      if (mainEntity.length > 0) {
        data.push({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": mainEntity
        });
      }
    }
    
    return data;
  }, [route, routeStartPlaceName, routeEndPlaceName, routeVideoAuthor, video, videos, profile, breadcrumbs, faqItems, canonicalUrl, pageImage, pageImageAbsolute, i18n.language, t, pathNormalized, includeWebSite, includeOrganization]);
  
  return (
    <Helmet>
      {/* Основные meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Альтернативные языки (hreflang) - динамически для всех доступных языков */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hreflang={lang} href={url} />
      ))}
      <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImageAbsolute} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:site_name" content="Travel Map Platform" />
      <meta property="og:locale" content={currentLang === 'ru' ? 'ru_RU' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLang === 'ru' ? 'en_US' : 'ru_RU'} />
      
      {/* Twitter Card: summary = компактное превью, summary_large_image = большое */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImageAbsolute} />
      <meta name="twitter:site" content="@travmap" />
      <meta name="twitter:creator" content="@travmap" />
      
      {/* JSON-LD структурированные данные */}
      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
      
      {/* Дополнительные meta tags из children */}
      {children}
    </Helmet>
  );
};

export default SEO;
